// src/PublicUser/pages/BlogDetailsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BlogNavbar from '../components/common/Navbar';
import BlogFooter from '../components/common/Footer';
import { BlogService } from '../../services/blogService';
import '../css/BlogDetails.css';
import ContentBlock from '../../components/ContentBlock';
import '../css/BlogDetailsHero.css';

// Fixed author data
const AUTHOR_DATA = {
  name: "Er. Akash GC",
  role: "Senior Developer / Part-time Lecturer",
  education: "Master in Computer Science",
  achievements: "Full Scholarship holder for BE and Master",
  image: "/src/img/aksh.png",
  bio: "Er. Akash GC is a Senior Developer and Part-time Lecturer with a Master's degree in Computer Science. He is a full scholarship holder for both his BE and Master's degrees. With years of experience in software development and academia, he brings practical insights and theoretical knowledge to every article.",
  social: {
    youtube: "https://www.youtube.com/@akashgc21",
    linkedin: "https://www.linkedin.com/in/akashgc/",
    github: "https://github.com/GcAkash"
  }
};

const BlogDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Fetch blog details
  const fetchBlogDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch the specific blog post by ID
      const response = await BlogService.getPostWithContent(id);
      const blogData = response.data || response;
      setBlog(blogData);
      
      // Fetch related posts based on category
      if (blogData.categoryId) {
        const relatedResponse = await BlogService.getBlogsByCategory(blogData.categoryId);
        const relatedData = relatedResponse.data || relatedResponse;
        // Filter out current post and limit to 3
        const filteredRelated = relatedData
          .filter(post => post.id !== parseInt(id))
          .slice(0, 3);
        setRelatedPosts(filteredRelated);
      }
      
      // Fetch popular posts
      const popularResponse = await BlogService.getBlogsSummery();
      const popularData = popularResponse.data || popularResponse;
      setPopularPosts(popularData.slice(0, 5));
      
    } catch (err) {
      setError(err.message || 'Failed to load blog post');
      console.error('Error fetching blog details:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBlogDetails();
    window.scrollTo(0, 0);
  }, [fetchBlogDetails]);

  // Handle scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate read time (approx 200 words per minute)
  const calculateReadTime = (contentBlocks) => {
    if (!contentBlocks) return '5 min read';
    
    let totalWords = 0;
    contentBlocks.forEach(block => {
      if (block.text) {
        totalWords += block.text.split(/\s+/).length;
      }
    });
    
    const readTime = Math.max(1, Math.ceil(totalWords / 200));
    return `${readTime} min read`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleImageClick = () => {
    setIsImageExpanded(!isImageExpanded);
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: blog.title,
        text: blog.description,
        url: window.location.href,
      });
    } catch (err) {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <>
        <div className="pro-details-loader-container">
          <div className="pro-details-loader"></div>
          <p>Loading amazing content...</p>
        </div>
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <div className="pro-details-error-container">
          <h2>⚠️ {error || 'Post not found'}</h2>
          <button onClick={() => navigate('/blog')} className="pro-back-btn">
            ← Back to Blog
          </button>
        </div>
      </>
    );
  }

  const readTime = calculateReadTime(blog.contentBlock);
  const formattedDate = formatDate(blog.createdAt);

  return (
    <>
      {/* Progress Bar */}
      <div className="pro-progress-bar">
        <div 
          className="pro-progress-fill" 
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
    {/* hero section */}
 <section className="techshara-hero-section">
  <div className="techshara-hero-container">
    
    <div className="techshara-hero-badge">
      <span className="techshara-badge-dot"></span>
      TechShara Insights
    </div>

    <h1 className="techshara-hero-title">
     Stories & <span className="techshara-gradient-text">Ideas</span>
    </h1>

    <p className="techshara-hero-description">
      Explore practical development guides, modern technologies, AI trends,
      and expert engineering insights from TechShara.
    </p>

    <div className="techshara-hero-button-wrapper">
      <a href="/blog" className="techshara-hero-button">
        View All Articles
      </a>
    </div>

  </div>
</section>


      <article className="pro-details-container">
        {/* Main Wrapper - Two Column Layout */}
        <div className="pro-details-main-wrapper">
          
          {/* Left Column - Main Content */}
          <div className="pro-main-column">
            {/* Hero Section with Title and Meta - No author at top */}
            <div className="pro-hero-section">
                <h2 className="pro-title">{blog.title}</h2>
              <div className="pro-meta-top">
                <span className="pro-category">{blog.category || 'Uncategorized'}</span>
                <span className="pro-date">
                  📅 {formattedDate}
                </span>
                <span className="pro-readtime">⏱️ {readTime}</span>
                <span className="pro-views">👁️ {(blog.views || 0).toLocaleString()} views</span>
              </div>

            </div>

            {/* Cover Image */}
            {blog.coverImage && (
              <div className={`pro-cover-wrapper ${isImageExpanded ? 'pro-expanded' : ''}`}>
                <div className="pro-cover-image-container">
                  <img 
                    src={blog.coverImage} 
                    alt={blog.title} 
                    onClick={handleImageClick}
                    className="pro-cover-image"
                  />
                  <button 
                    className="pro-image-adjust-btn" 
                    onClick={handleImageClick}
                    aria-label="Adjust image size"
                  >
                    {isImageExpanded ? '⟳' : '⬚'}
                  </button>
                  <div className="pro-image-hint">
                    Click to {isImageExpanded ? 'reduce' : 'expand'}
                  </div>
                </div>
              </div>
            )}

            {/* Blog Content - Dynamic from ContentBlock */}
            <div className="pro-content-body">
              {/* Description as intro */}
              {blog.description && (
                <div className="pro-description">
                  <p className="pro-description-text">{blog.description}</p>
                </div>
              )}
              
              {/* Content Blocks */}
              <div className="learn-content-simple">
                {Array.isArray(blog.contentBlock) && blog.contentBlock.length > 0 ? (
                  blog.contentBlock
                    .sort((a, b) => a.order - b.order)
                    .map((block) => (
                      <div key={block.id} className="learn-content-block-wrapper">
                        <ContentBlock block={block} />
                      </div>
                    ))
                ) : (
                  <div className="learn-placeholder-content">
                    <div className="learn-placeholder-wave">
                      <div className="learn-wave-bar"></div>
                      <div className="learn-wave-bar"></div>
                      <div className="learn-wave-bar"></div>
                      <div className="learn-wave-bar"></div>
                    </div>
                    <p>Content is being prepared...</p>
                  </div>
                )}
              </div>
              
              {/* Author Bio - At the end with fixed data */}
              <div className="pro-author-bio">
                <img 
                  src={AUTHOR_DATA.image} 
                  alt={AUTHOR_DATA.name} 
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(AUTHOR_DATA.name)}&background=0066FF&color=fff`;
                  }}
                />
                <div className="pro-bio-content">
                  <h4>About {AUTHOR_DATA.name}</h4>
                  <p className="pro-bio-role">{AUTHOR_DATA.role}</p>
                  <p className="pro-bio-education">{AUTHOR_DATA.education}</p>
                  <p className="pro-bio-achievements">{AUTHOR_DATA.achievements}</p>
                  <p className="pro-bio-description">{AUTHOR_DATA.bio}</p>
                  <div className="pro-bio-social">
                    <a href={AUTHOR_DATA.social.youtube} target="_blank" rel="noopener noreferrer">Youtube</a>
                    <a href={AUTHOR_DATA.social.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>
                    <a href={AUTHOR_DATA.social.github} target="_blank" rel="noopener noreferrer">GitHub</a>
                  </div>
                </div>
              </div>
              
              {/* Engagement Buttons */}
              <div className="pro-engagement">
                {/* <button className="pro-btn pro-like">❤️ 0 Likes</button> */}
                <button className="pro-btn pro-share" onClick={handleShare}>🔗 Share</button>
                <button className="pro-btn pro-save">🔖 Save</button>
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="pro-sidebar-column">
            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <div className="pro-sidebar-card">
                <h3 className="pro-sidebar-title">
                  <span>🔗</span> Related Posts
                </h3>
                <div className="pro-related-list">
                  {relatedPosts.map((post) => (
                    <div 
                      key={post.id} 
                      className="pro-related-item"
                      onClick={() => navigate(`/blog/${post.id}`)}
                    >
                      <img src={post.coverImageUrl} alt={post.title} />
                      <div className="pro-related-info">
                        <h4>{post.title}</h4>
                        <div className="pro-related-meta">
                          <span>{post.category}</span>
                          <span>{calculateReadTime(post.contentBlock)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Most Popular */}
            {popularPosts.length > 0 && (
              <div className="pro-sidebar-card">
                <h3 className="pro-sidebar-title">
                  <span>🔥</span> Most Popular
                </h3>
                <div className="pro-popular-list">
                  {popularPosts.map((post, index) => (
                    <div 
                      key={post.id} 
                      className="pro-popular-item"
                      onClick={() => navigate(`/blog/${post.id}`)}
                    >
                      <div className="pro-popular-rank">#{index + 1}</div>
                      <img src={post.coverImageUrl} alt={post.title} />
                      <div className="pro-popular-info">
                        <h4>{post.title}</h4>
                        <div className="pro-popular-meta">
                          <span>👁️ {(post.views || 0).toLocaleString()}</span>
                          <span>{post.category}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Newsletter */}
            <div className="pro-sidebar-card pro-newsletter-card">
              <h3 className="pro-sidebar-title">📧 Newsletter</h3>
              <p>Get the latest posts delivered to your inbox</p>
              <input type="email" placeholder="Your email address" />
              <button>Subscribe →</button>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogDetailsPage;